/**
 * Google Fonts: solo la familia elegida en text_typography (mismo contrato que identity-admin).
 * Proxima Nova es la fuente por defecto del DS (Infinity / assets locales): no se inyecta ninguna hoja.
 */

const LINK_MARKER = 'data-pwcs-google-fonts';

const WEIGHT_AXIS = 'wght@300;400;500;600;700';

/**
 * Opciones de tipografía del proceso (id = valor enviado en css_config / text_typography).
 * Proxima Nova → sin descarga remota (fuente local del DS).
 */
export const PROCESS_TYPOGRAPHY_STACK_OPTIONS = [
	{ label: 'Proxima Nova', id: 'proxima-nova, sans-serif' },
	{ label: 'Poppins', id: 'Poppins, sans-serif' },
	{ label: 'Roboto', id: 'Roboto, sans-serif' },
	{ label: 'Open Sans', id: '"Open Sans", sans-serif' },
	{ label: 'Lato', id: 'Lato, sans-serif' },
	{ label: 'Montserrat', id: 'Montserrat, sans-serif' },
	{ label: 'Arial', id: 'Arial, sans-serif' },
] as const;

/**
 * Primera familia del stack (minúsculas, sin comillas) → slug Google Fonts CSS2, o null si es Proxima / Arial / no web.
 */
const FIRST_FAMILY_TO_GOOGLE_SLUG: Readonly<Record<string, string | null>> = {
	'proxima-nova': null,
	'proxima nova': null,
	arial: null,
	poppins: 'Poppins',
	roboto: 'Roboto',
	'open sans': 'Open+Sans',
	lato: 'Lato',
	montserrat: 'Montserrat',
};

function parseFirstFontFamily(stack: string): string | null {
	const trimmed = stack.trim();
	if (!trimmed) {
		return null;
	}
	const first = trimmed.split(',')[0];
	const cleaned = first
		.trim()
		.replace(/^["']|["']$/g, '')
		.trim();
	return cleaned.length > 0 ? cleaned : null;
}

/**
 * Resuelve la URL de una sola familia Google Fonts, o null si no hace falta cargar nada.
 */
export function resolveGoogleFontsStylesheetHref(fontStack: string): string | null {
	const first = parseFirstFontFamily(fontStack);
	if (!first) {
		return null;
	}
	const key = first.toLowerCase();
	const slug = FIRST_FAMILY_TO_GOOGLE_SLUG[key];
	if (slug === undefined) {
		return null;
	}
	if (slug === null) {
		return null;
	}
	return `https://fonts.googleapis.com/css2?family=${slug}:${WEIGHT_AXIS}&display=swap`;
}

function appendPreconnectOnce(doc: Document, href: string, crossOrigin?: 'anonymous'): void {
	const links = doc.head.querySelectorAll<HTMLLinkElement>('link[rel="preconnect"]');
	for (let i = 0; i < links.length; i++) {
		const link = links.item(i);
		if (link && (link.href === href || link.getAttribute('href') === href)) {
			return;
		}
	}
	const pre = doc.createElement('link');
	pre.rel = 'preconnect';
	pre.href = href;
	if (crossOrigin) {
		pre.crossOrigin = crossOrigin;
	}
	doc.head.appendChild(pre);
}

/**
 * Inserta, sustituye o elimina la hoja de Google Fonts según el stack (una sola familia o ninguna).
 */
export function syncGoogleFontsStylesheet(doc: Document, fontStack: string): void {
	const href = resolveGoogleFontsStylesheetHref(fontStack);
	const existing = doc.querySelector<HTMLLinkElement>(`link[${LINK_MARKER}]`);

	if (!href) {
		existing?.remove();
		return;
	}

	if (existing?.getAttribute('data-pwcs-font-href') === href) {
		return;
	}
	existing?.remove();

	appendPreconnectOnce(doc, 'https://fonts.googleapis.com');
	appendPreconnectOnce(doc, 'https://fonts.gstatic.com', 'anonymous');

	const link = doc.createElement('link');
	link.rel = 'stylesheet';
	link.href = href;
	link.setAttribute(LINK_MARKER, '');
	link.setAttribute('data-pwcs-font-href', href);
	doc.head.prepend(link);
}
